import * as _ from 'lodash';
import { remunerationTypes } from '../../objects/Payslip';
import { EmployerOptions } from '../EmployerOptions';
import { PaymentGroupModel, paymentGroupInstanceTypes } from '../PaymentGroupModel';
import { PaymentModel } from '../PaymentModel';

/**
 * @typedef { import('../../objects/Claim').Claim } Claim
 * @typedef { import('../PayslipModel').PayslipModel } PayslipModel
 * @typedef { import('../calculation/PolitikerCalculator').PolitikerCalculator } PolitikerCalculator
 */

const proposedState = Object.freeze({
    initial: 0,
    accepted: 1,
    rejecting: 2,
    rejected: 3,
});

export class ProposedGroupModel extends PaymentGroupModel {

    /**
     * @param {PayslipModel} payslipModel
     * @param {PolitikerCalculator} calculator
     */
    constructor(payslipModel, calculator) {
        super(payslipModel);

        this.calculator = calculator;

        this._state = proposedState.initial;
        this.rejectionMessage = '';

        /** @type {EmployerOptions} */
        this.selectedEmployer = null;
        /** @type {EmployerOptions[]} */
        this.availableEmployers = null;
    }

    checkChanges() {
        return this.hasResponded;
    }

    get paymentGroupInstanceType() {
        return paymentGroupInstanceTypes.proposedClaim;
    }

    get isInitial() {
        return this._state === proposedState.initial;
    }

    get hasAccepted() {
        return this._state === proposedState.accepted;
    }

    get hasRejected() {
        return this._state === proposedState.rejected;
    }

    get isRejecting() {
        return this._state === proposedState.rejecting;
    }

    get hasResponded() {
        return this.hasAccepted || this.hasRejected;
    }

    get canAccept() {
        return this.availableEmployers == null || this.selectedEmployer != null;
    }

    _setState(state) {
        if (this._state !== state) {
            this._state = state;
            this.payslipModel.paymentGroupChanged(this);
        }
    }

    accept() {
        this._setState(proposedState.accepted);
    }

    rejecting() {
        this._setState(proposedState.rejecting);
    }

    rejected() {
        this._setState(proposedState.rejected);
    }

    reset() {
        this._setState(proposedState.initial);
    }

    /**
     */
    recalculate() {
        if (this.calculator != null) {
            let calculated = this.calculator.calculateClaim(this.payslipModel.attendance, this.claim, this.selectedEmployer);
            this.payments = _.map(calculated, x => PaymentModel.fromCalculated(this, x));
            this.payslipModel.paymentGroupChanged(this);
        }
    }

    createAdd() {
        if (!this.hasAccepted) return null;

        let result = {
            RemunerationType: remunerationTypes.claim,
            AcceptsClaim: this.claim.Id,
            Payments: this.payments.map(x => x.payment),
        };

        return result;
    }
    
    /**
     * @param {PayslipModel} payslipModel
     * @param {Claim} claim
     * @param {PolitikerCalculator} calculator
     */
    static create(payslipModel, claim, calculator) {

        let result = new ProposedGroupModel(payslipModel, calculator);

        result.claim = claim;

        if (calculator.isEmployerDependent(claim)) {
            result.availableEmployers = EmployerOptions.fromAttendance(payslipModel.attendance);
            if (result.availableEmployers.length === 1) {
                result.selectedEmployer = result.availableEmployers[0];
            }
        }

        if (calculator != null) {
            let calculatedPayments = calculator.calculateClaim(payslipModel.attendance, claim, result.selectedEmployer);
            result.payments = _.map(calculatedPayments, x => PaymentModel.fromCalculated(result, x));
        }

        result.sortPayments();

        return result;
    }
}
